import { Spinner } from '@frontend/components/common';
import { ICONS, Plus } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { UserSelectV2 } from '@frontend/user/select';
import { User } from '@frontend/user/utils';
import { FormattedMessage } from 'react-intl';

import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import useSelectReceivers from './select-receivers.controller';

const ID = 'select-receivers';

const SelectReceivers = () => {
    const viewProps = useSelectReceivers();

    return (
        <WizardScreenContainer
            id={ID}
            footer={{
                onSubmit: viewProps.onSubmit,
                submitDisabled: !viewProps.canContinue,
                submitLabel: CommonMessage.BUTTONS.CONTINUE
            }}
            containerClassName='relative flex w-full h-full flex-col content-stretch items-start gap-6 p-6'>
            <p className='text-foreground-default relative w-full shrink-0 text-base leading-normal font-medium'>
                <FormattedMessage
                    id='Wizard.SelectReceivers.Instructions'
                    defaultMessage='Please select the receiver(s)'
                    description='Instructions for receiver selection'
                />
            </p>
            {viewProps.delayedReceiversSearch ? (
                <p className='text-brand-muted relative w-full shrink-0 text-sm leading-normal font-medium'>
                    <FormattedMessage
                        id='kiosk.wizard.select-receivers.delayed-search-hint'
                        defaultMessage='Type at least 3 characters to search'
                        description='Hint shown on kiosk select-receivers when delayed_receivers_search is enabled'
                    />
                </p>
            ) : null}

            {viewProps.isLoading ? (
                <Spinner />
            ) : (
                <div className='flex w-full flex-col gap-3'>
                    {viewProps.selectedReceivers.map((receiver, index) => (
                        <div
                            key={index + '-' + (receiver ? receiver : '')}
                            className='flex w-full items-center gap-3'>
                            <div className='flex-1'>
                                <UserSelectV2
                                    id={index.toString()}
                                    hideLabel
                                    value={receiver}
                                    onChange={(user) => viewProps.updateReceiver(index, user)}
                                    isClearable
                                    {...(viewProps.delayedReceiversSearch ? { minSearchCharacters: 3 } : {})}
                                    customLabel={(user) => new User(user).getDisplayName()}
                                    fetchOverwrite={viewProps.fetchReceiverOptions}
                                    subset={viewProps.receiverSubsets[index]}
                                />
                            </div>
                            {viewProps.selectedReceivers.length > 1 && (
                                <button
                                    onClick={() => viewProps.removeReceiver(index)}
                                    className='text-foreground-default hover:text-foreground-emphasis shrink-0 transition-colors'
                                    aria-label='Remove receiver'>
                                    <ICONS.BUTTON.DELETE className='size-5' />
                                </button>
                            )}
                        </div>
                    ))}

                    <button
                        type='button'
                        onClick={viewProps.addReceiver}
                        className='flex w-full items-center justify-end gap-3'>
                        <Plus className='size-3.5' />
                        <p className='text-foreground-default text-base leading-normal font-medium'>
                            <FormattedMessage
                                id='Wizard.SelectReceivers.AddAnother'
                                defaultMessage='Add another receiver'
                                description='Button to add another receiver'
                            />
                        </p>
                    </button>
                </div>
            )}

            {viewProps.showUnknownReceiver ? (
                <button
                    type='button'
                    className='border-brand-primary text-brand-primary bg-background-surface mt-auto flex w-full shrink-0 items-center justify-center rounded-xl border px-4 py-3 text-base font-medium'
                    onClick={() => {
                        void viewProps.onUnknownReceiver();
                    }}>
                    <FormattedMessage
                        id='kiosk.wizard.select-receivers.unknown-receiver'
                        defaultMessage='Unknown receiver'
                        description='Button to skip receiver selection and use the configured default receiver on kiosk select-receivers'
                    />
                </button>
            ) : null}
        </WizardScreenContainer>
    );
};

export default SelectReceivers;
